import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useQuery } from '@tanstack/react-query';
import { fetchUserSettings, fetchLinkedAccounts, fetchAllBotRules } from '../../services/api';
import { useUserContext } from '../../hooks/useUserContext';
import { UserAwareCacheKeys } from '../../utils/userAwareCacheKeys';

const CRISP_WEBSITE_ID = 'e7402b28-3fb6-468a-aef1-4df5f7ec79c6';

export const CrispChat: React.FC = () => {
  const { isAuthenticated, user } = useAuth0();
  const location = useLocation();
  const { isApiAuthReady, userBillingName } = useAuthStore(state => ({
    isApiAuthReady: state.isApiAuthReady,
    userBillingName: state.userBillingName
  }));
  const { userId, isReady: isUserContextReady } = useUserContext();

  // Onboarding picker state
  const [hasShownWelcome, setHasShownWelcome] = useState(false);
  const [previousSetupComplete, setPreviousSetupComplete] = useState<boolean | null>(null);

  // Fetch user settings to get subscription info
  const { data: userSettings, isLoading: isLoadingSettings } = useQuery({
    queryKey: userId ? UserAwareCacheKeys.userSettings(userId) : ['disabled'],
    queryFn: fetchUserSettings,
    enabled: isAuthenticated && isApiAuthReady && isUserContextReady && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch linked accounts for setup progress tracking
  const { data: linkedAccounts, isLoading: isLoadingAccounts } = useQuery({
    queryKey: userId ? UserAwareCacheKeys.linkedAccounts(userId) : ['disabled'],
    queryFn: fetchLinkedAccounts,
    enabled: isAuthenticated && isApiAuthReady && isUserContextReady && !!userId,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch bot rules for setup progress tracking (FIXED: use 'botRules' key)
  const { data: botRulesData, isLoading: isLoadingBotRules } = useQuery({
    queryKey: userId ? UserAwareCacheKeys.botRules(userId) : ['disabled'],
    queryFn: fetchAllBotRules,
    enabled: isAuthenticated && isApiAuthReady && isUserContextReady && !!userId,
    staleTime: 5 * 60 * 1000,
  });

  // Initialize Crisp SDK
  useEffect(() => {
    // Only initialize once
    if (window.$crisp) {
      console.log('[CrispChat] Already initialized');
      return;
    }

    console.log('[CrispChat] Initializing Crisp SDK');
    
    // Create Crisp script tag
    window.$crisp = [];
    window.CRISP_WEBSITE_ID = CRISP_WEBSITE_ID;

    const script = document.createElement('script');
    script.src = 'https://client.crisp.chat/l.js';
    script.async = true;
    document.getElementsByTagName('head')[0].appendChild(script);

    // Apply custom styling after Crisp loads
    script.onload = () => {
      console.log('[CrispChat] SDK loaded successfully');
      applyCrispStyling();
      
      // Set up listener for when Crisp chat is ready
      if (window.$crisp) {
        window.$crisp.push(['on', 'chat:opened', () => {
          console.log('[CrispChat] Chat opened - applying styles');
          setTimeout(() => injectCustomCrispCSS(), 100);
        }]);
        
        window.$crisp.push(['on', 'session:loaded', () => {
          console.log('[CrispChat] Session loaded - applying styles');
          setTimeout(() => injectCustomCrispCSS(), 100);
        }]);

        // Set up picker click listener for onboarding navigation
        window.$crisp.push(['on', 'message:received', (data: any) => {
          // Skip non-update messages (actual conversation messages)
          if (data.origin !== 'update') return;
          
          // Skip non-picker messages
          if (data.type !== 'picker') return;
          
          // Skip other pickers
          if (data.content?.id !== 'onboarding-steps') return;
          
          // Get selected choice
          const selected = data.content.choices?.find((choice: any) => choice.selected);
          if (!selected) return;
          
          console.log('[CrispChat] Onboarding picker clicked:', selected.value);
          
          // Navigate to selected step using custom event
          const stepMap: Record<string, number> = {
            'welcome': 1,
            'accounts': 2,
            'strategies': 3,
            'completion': 4,
          };
          
          const stepId = stepMap[selected.value];
          if (!stepId) {
            console.warn('[CrispChat] Unknown step value:', selected.value);
            return;
          }
          
          // Check if user is on onboarding page
          const isOnOnboarding = window.location.pathname === '/onboarding';
          
          if (isOnOnboarding) {
            // Already on onboarding - dispatch custom event for navigation
            window.dispatchEvent(new CustomEvent('crisp-navigate-step', { 
              detail: { stepId } 
            }));
            
            // Show feedback in Crisp
            if (window.$crisp) {
              const stepNames: Record<number, string> = {
                1: 'Welcome',
                2: 'Connect Accounts',
                3: 'Define Strategies',
                4: 'Complete Setup'
              };
              window.$crisp.push(['do', 'message:show', ['text', 
                `📍 Navigating to ${stepNames[stepId]}...`
              ]]);
            }
          } else {
            // On Dashboard or other page - navigate to onboarding first
            sessionStorage.setItem('crisp-target-step', stepId.toString());
            window.location.href = '/onboarding?fromCrisp=true';
          }
        }]);
      }
      
      // Multiple retry attempts to catch Crisp's dynamic loading
      const retryTimes = [500, 1000, 2000, 3000, 5000];
      retryTimes.forEach((delay, index) => {
        setTimeout(() => {
          console.log(`[CrispChat] Retry ${index + 1} styling injection`);
          injectCustomCrispCSS();
        }, delay);
      });
    };

    // Set up observer for BlendingSpinner visibility changes
    const spinnerObserver = new MutationObserver(() => {
      const hasSpinner = document.querySelector('.loading-transition');
      const crispClient = document.querySelector('.crisp-client') as HTMLElement;
      
      if (crispClient) {
        if (hasSpinner) {
          console.log('[CrispChat] BlendingSpinner detected - lowering z-index and disabling interaction');
          crispClient.style.setProperty('z-index', '9997', 'important');
          crispClient.style.setProperty('pointer-events', 'none', 'important');
        } else {
          console.log('[CrispChat] BlendingSpinner removed - restoring z-index and interaction');
          crispClient.style.setProperty('z-index', '9997', 'important');
          crispClient.style.setProperty('pointer-events', 'auto', 'important');
        }
      }
    });

    // Start observing body for spinner changes
    spinnerObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    return () => {
      // Cleanup on unmount
      spinnerObserver.disconnect();
      
      if (window.$crisp) {
        try {
          window.$crisp.push(['do', 'chat:hide']);
        } catch (error) {
          console.warn('[CrispChat] Cleanup error:', error);
        }
      }
    };
  }, []);

  // Update user context when authentication state changes
  useEffect(() => {
    if (!window.$crisp) return;

    if (isAuthenticated && user) {
      console.log('[CrispChat] Setting authenticated user context');
      
      // Set user email
      if (user.email) {
        window.$crisp.push(['set', 'user:email', [user.email]]);
      }

      // Set user nickname (billing name or Auth0 name)
      const displayName = userBillingName || user.name || 'User';
      window.$crisp.push(['set', 'user:nickname', [displayName]]);

      // Determine subscription tier from user roles
      const roles = (user['https://botblender.com/roles'] || []) as string[];
      const subscriptionTier = getSubscriptionTier(roles);

      // Set user segments for routing
      const segments = ['customer', 'authenticated'];
      if (subscriptionTier === 'elite') segments.push('elite');
      if (subscriptionTier === 'trial') segments.push('trial');
      if (roles.includes('Admin')) segments.push('admin');

      window.$crisp.push(['set', 'session:segments', [segments]]);

      // Calculate setup progress
      const hasAccounts = Boolean(linkedAccounts && linkedAccounts.length > 0);
      const hasStrategies = Boolean(botRulesData && botRulesData.bots && botRulesData.bots.length > 0);
      const isComplete = Boolean(userSettings?.is_setup_complete);
      
      const setupPercentage = calculateSetupPercentage(hasAccounts, hasStrategies, isComplete);
      const currentStep = getCurrentOnboardingStep(hasAccounts, hasStrategies, isComplete);

      // Set custom data for agents to see
      window.$crisp.push(['set', 'session:data', [[
        ['subscription_tier', subscriptionTier],
        ['user_id', user.sub || 'unknown'],
        ['setup_complete', isComplete ? 'yes' : 'no'],
        ['setup_percentage', setupPercentage.toString()],
        ['onboarding_step', currentStep.toString()],
        ['linked_accounts', (linkedAccounts?.length || 0).toString()],
        ['strategies_count', (botRulesData?.bots?.length || 0).toString()],
        ['platform', 'web']
      ]]]);

      console.log('[CrispChat] User context set:', {
        email: user.email,
        name: displayName,
        tier: subscriptionTier,
        segments,
        setupProgress: {
          percentage: setupPercentage,
          step: currentStep,
          hasAccounts,
          hasStrategies,
          isComplete
        }
      });
    } else {
      console.log('[CrispChat] Setting visitor context');
      
      // For unauthenticated users, set visitor segment
      window.$crisp.push(['set', 'session:segments', [['visitor', 'public']]]);
      
      // Clear user data
      window.$crisp.push(['set', 'user:email', [null]]);
      window.$crisp.push(['set', 'user:nickname', [null]]);
    }
  }, [isAuthenticated, user, userBillingName, userSettings]);

  // Display onboarding picker when on /onboarding or dashboard and setup incomplete
  useEffect(() => {
    if (!window.$crisp || !isAuthenticated || !isApiAuthReady) return;
    if (isLoadingSettings || isLoadingAccounts || isLoadingBotRules) return;

    const currentPath = location.pathname;
    const isOnOnboarding = currentPath === '/onboarding';
    const isInDashboard = currentPath.startsWith('/dashboard') || 
                          currentPath === '/settings' || 
                          currentPath === '/bot-maintenance';

    const hasLinkedAccounts = Boolean(linkedAccounts && linkedAccounts.length > 0);
    const hasBotRules = Boolean(botRulesData?.bots && botRulesData.bots.length > 0);
    const isSetupComplete = Boolean(userSettings?.is_setup_complete);

    // Determine if picker should show (matches SetupChecklistBanner logic)
    const shouldShowPicker = 
      (isOnOnboarding || isInDashboard) &&
      isSetupComplete === false &&
      (!hasLinkedAccounts || !hasBotRules);

    if (shouldShowPicker) {
      // Show welcome message first on /onboarding (once per session)
      if (isOnOnboarding && !hasShownWelcome) {
        const welcomeShown = sessionStorage.getItem('crisp-welcome-shown');
        if (!welcomeShown) {
          setTimeout(() => {
            window.$crisp?.push(['do', 'message:show', ['text', 
              '👋 Welcome to BotBlender! I\'m here to help guide you through setup.\nClick any step below to jump directly to it:'
            ]]);
            sessionStorage.setItem('crisp-welcome-shown', 'true');
            setHasShownWelcome(true);
            
            // Show picker after welcome message
            setTimeout(() => {
              showOnboardingPicker(hasLinkedAccounts, hasBotRules, isSetupComplete);
            }, 500);
          }, 2000);
        } else {
          // Welcome already shown, just show picker
          setTimeout(() => {
            showOnboardingPicker(hasLinkedAccounts, hasBotRules, isSetupComplete);
          }, 2000);
        }
      } else if (isInDashboard) {
        // On Dashboard: show immediately when Crisp opens
        showOnboardingPicker(hasLinkedAccounts, hasBotRules, isSetupComplete);
      }
    }
  }, [
    location.pathname,
    isAuthenticated,
    isApiAuthReady,
    isLoadingSettings,
    isLoadingAccounts,
    isLoadingBotRules,
    userSettings?.is_setup_complete,
    linkedAccounts?.length,
    botRulesData?.bots?.length,
    hasShownWelcome
  ]);

  // Handle completion celebration
  useEffect(() => {
    if (!window.$crisp) return;

    // Detect completion transition
    if (userSettings?.is_setup_complete === true && 
        previousSetupComplete === false) {
      
      console.log('[CrispChat] Setup completed! Showing celebration message');
      
      // Show success message
      if (window.$crisp) {
        window.$crisp.push(['do', 'message:show', ['text', 
          '🎉 Congratulations! Your setup is complete. Welcome to BotBlender!'
        ]]);
        
        setTimeout(() => {
          if (window.$crisp) {
            window.$crisp.push(['do', 'message:show', ['text', 
              'Feel free to reach out if you need any help!'
            ]]);
          }
        }, 1000);
      }
    }
    
    // Track previous state
    setPreviousSetupComplete(userSettings?.is_setup_complete ?? null);
  }, [userSettings?.is_setup_complete, previousSetupComplete]);

  return null; // Component doesn't render anything visible
};

// Show onboarding picker with dynamic choices
function showOnboardingPicker(
  hasAccounts: boolean,
  hasStrategies: boolean,
  isComplete: boolean
) {
  if (!window.$crisp) return;

  // Detect mobile viewport
  const isMobile = window.innerWidth < 768;

  const choices = [];
  
  // Only show incomplete steps (Welcome is hidden, always complete)
  if (!hasAccounts) {
    choices.push({
      value: 'accounts',
      icon: '🔗',
      label: isMobile ? 'Accounts' : 'Connect Accounts (Step 1)',
      selected: false
    });
  }
  
  if (!hasStrategies) {
    choices.push({
      value: 'strategies',
      icon: '🎯',
      label: isMobile ? 'Strategies' : 'Define Strategies (Step 2)',
      selected: false
    });
  }
  
  if (!isComplete) {
    choices.push({
      value: 'completion',
      icon: '✅',
      label: isMobile ? 'Complete' : 'Complete Setup (Step 3)',
      selected: false
    });
  }

  if (choices.length === 0) {
    console.log('[CrispChat] No incomplete steps, skipping picker display');
    return;
  }

  console.log('[CrispChat] Showing onboarding picker with', choices.length, 'choices', isMobile ? '(mobile)' : '(desktop)');

  // Display the picker
  window.$crisp.push(['do', 'message:show', ['picker', {
    id: 'onboarding-steps',
    text: '🚀 Your Setup Progress - Click to continue:',
    choices: choices
  }]]);
}

// Calculate setup completion percentage based on onboarding steps
function calculateSetupPercentage(
  hasAccounts: boolean,
  hasStrategies: boolean,
  isComplete: boolean
): number {
  let completed = 1; // Welcome step always completed
  if (hasAccounts) completed++;
  if (hasStrategies) completed++;
  if (isComplete) completed++;
  return Math.round((completed / 4) * 100);
}

// Get current onboarding step (1-5)
function getCurrentOnboardingStep(
  hasAccounts: boolean,
  hasStrategies: boolean,
  isComplete: boolean
): number {
  if (!hasAccounts) return 2; // Connect Accounts
  if (!hasStrategies) return 3; // Define Strategies
  if (!isComplete) return 4; // Configure Engine
  return 5; // Completed
}

// Helper to determine subscription tier from Auth0 roles
function getSubscriptionTier(roles: string[]): string {
  if (!roles || roles.length === 0) return 'none';
  
  if (roles.includes('Elite')) return 'elite';
  if (roles.includes('Standard')) return 'standard';
  if (roles.includes('Basic')) return 'basic';
  if (roles.includes('Trial')) return 'trial';
  
  return 'none';
}

// Apply custom Crisp styling to match BotBlender design
function applyCrispStyling() {
  if (!window.$crisp) return;

  console.log('[CrispChat] Applying custom styling via Crisp config');

  // CRITICAL: Use Crisp's native color configuration
  // This WILL work because it's Crisp's own API
  window.$crisp.push(['config', 'color:theme', ['#6366f1']]); // Indigo-500 (main theme color)
  
  // Additional config options
  window.$crisp.push(['config', 'position:reverse', [false]]); // Bottom right
  window.$crisp.push(['config', 'hide:on:mobile', [false]]); // Show on mobile
  
  // Try to set safe mode (prevents script injection but allows styling)
  window.$crisp.push(['safe', true]);

  console.log('[CrispChat] Native color config applied: #6366f1');

  // Inject custom CSS for glassmorphism effect
  injectCustomCrispCSS();
  
  // Set up a mutation observer to re-apply styles if Crisp modifies the DOM
  setupCrispStyleObserver();
}

// Inject custom CSS for glassmorphism styling
function injectCustomCrispCSS() {
  // Remove existing styles if present to force re-injection
  const existingStyle = document.getElementById('crisp-custom-styles');
  if (existingStyle) {
    console.log('[CrispChat] Removing existing custom styles for re-injection');
    existingStyle.remove();
  }

  console.log('[CrispChat] Injecting custom CSS...');
  
  // CRITICAL: Lower Crisp z-index when BlendingSpinner is visible
  // Check for loading-transition class which indicates spinner is active
  const hasLoadingSpinner = document.querySelector('.loading-transition');
  if (hasLoadingSpinner) {
    console.log('[CrispChat] BlendingSpinner detected - lowering Crisp z-index');
  }
  
  // Debug: Log what Crisp elements exist
  const crispElements = document.querySelectorAll('[class*="crisp"], [class*="cc-"]');
  console.log(`[CrispChat] Found ${crispElements.length} Crisp elements in DOM`);
  if (crispElements.length > 0) {
    console.log('[CrispChat] Sample classes:', Array.from(crispElements).slice(0, 5).map(el => el.className));
  }

  // CRITICAL FIX: Override inline CSS variables directly on the element
  // This is more powerful than CSS selectors because it modifies the inline style
  const crispClient = document.querySelector('.crisp-client');
  if (crispClient && crispClient instanceof HTMLElement) {
    console.log('[CrispChat] Overriding CSS variables directly on .crisp-client element');
    
    // Set CSS variables directly on the element's style (overrides inline styles)
    crispClient.style.setProperty('--crisp-color-theme-500', '99, 102, 241', 'important');
    crispClient.style.setProperty('--crisp-color-theme-600', '79, 70, 229', 'important');
    crispClient.style.setProperty('--crisp-color-theme-700', '67, 56, 202', 'important');
    crispClient.style.setProperty('--crisp-color-theme-800', '55, 48, 163', 'important');
    crispClient.style.setProperty('--crisp-color-theme-900', '49, 46, 129', 'important');
    crispClient.style.setProperty('--crisp-color-theme-10', '238, 242, 255', 'important');
    crispClient.style.setProperty('--crisp-color-theme-20', '224, 231, 255', 'important');
    crispClient.style.setProperty('--crisp-color-theme-100', '199, 210, 254', 'important');
    crispClient.style.setProperty('--crisp-color-theme-200', '129, 140, 248', 'important');
    crispClient.style.setProperty('--crisp-color-theme-reverse', '255, 255, 255', 'important');
    
    console.log('[CrispChat] ✅ CSS variables overridden successfully');
  } else {
    console.warn('[CrispChat] ⚠️ Could not find .crisp-client element to override variables');
  }

  const style = document.createElement('style');
  style.id = 'crisp-custom-styles';
  style.innerHTML = `
    /* ===== Z-INDEX MANAGEMENT ===== */
    /* Lower Crisp z-index when BlendingSpinner is active */
    /* BlendingSpinner uses z-[999999], so Crisp must be below that */
    .crisp-client {
      z-index: 9997 !important;
    }
    
    /* When loading spinner is present, ensure Crisp stays below */
    body:has(.loading-transition) .crisp-client,
    body:has([class*="z-[999999]"]) .crisp-client {
      z-index: 9997 !important;
      pointer-events: none !important;
    }
    
    /* Re-enable pointer events when no spinner */
    body:not(:has(.loading-transition)) .crisp-client {
      pointer-events: auto !important;
    }
    
    /* Glassmorphism styling for Crisp chat widget */
    /* CRITICAL FIX: Crisp uses CSS custom properties for theming */
    /* We must override these variables to change the colors */
    
    /* ===== CSS VARIABLE OVERRIDES (THE ACTUAL FIX!) ===== */
    .crisp-client[data-color-mode="light"],
    .crisp-client[data-color-mode="dark"],
    .crisp-client {
      /* Primary theme colors - Indigo (#6366f1) instead of Crisp blue (#1972F5) */
      --crisp-color-theme-500: 99, 102, 241 !important;  /* rgb(99, 102, 241) = #6366f1 Indigo-500 */
      --crisp-color-theme-600: 79, 70, 229 !important;   /* rgb(79, 70, 229) = #4f46e5 Indigo-600 */
      --crisp-color-theme-700: 67, 56, 202 !important;   /* rgb(67, 56, 202) = #4338ca Indigo-700 */
      --crisp-color-theme-800: 55, 48, 163 !important;   /* rgb(55, 48, 163) = #3730a3 Indigo-800 */
      --crisp-color-theme-900: 49, 46, 129 !important;   /* rgb(49, 46, 129) = #312e81 Indigo-900 */
      
      /* Light background colors (subtle indigo tints) */
      --crisp-color-theme-10: 238, 242, 255 !important;  /* Very light indigo */
      --crisp-color-theme-20: 224, 231, 255 !important;  /* Light indigo */
      --crisp-color-theme-100: 199, 210, 254 !important; /* Lighter indigo */
      --crisp-color-theme-200: 129, 140, 248 !important; /* Light-mid indigo */
      
      /* White for text on colored backgrounds */
      --crisp-color-theme-reverse: 255, 255, 255 !important;
    }
    
    /* ===== OVERRIDE ALL CRISP BLUE COLORS ===== */
    .crisp-client div[style*="background"],
    .crisp-client div[style*="background-color"],
    .crisp-client button[style*="background"],
    .crisp-client button[style*="background-color"],
    .crisp-client a[style*="background"],
    .crisp-client a[style*="background-color"] {
      background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%) !important;
      background-color: #6366f1 !important;
    }
    
    /* Override inline blue styles */
    .crisp-client [style*="rgb(39, 119, 255)"],
    .crisp-client [style*="rgb(37, 99, 235)"],
    .crisp-client [style*="#2563eb"],
    .crisp-client [style*="#0066ff"] {
      background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%) !important;
      background-color: #6366f1 !important;
    }
    
    /* ===== CHAT BUBBLE BUTTON ===== */
    /* Target the floating chat button */
    .crisp-client[data-crisp-loaded="true"] .crisp-1rjpbb7,
    .crisp-client .cc-unoo,
    .crisp-client .crisp-1rjpbb7 > div,
    div[class*="crisp-"][class*="unoo"] {
      background: linear-gradient(135deg, rgba(99, 102, 241, 0.9), rgba(139, 92, 246, 0.9)) !important;
      backdrop-filter: blur(12px) !important;
      -webkit-backdrop-filter: blur(12px) !important;
      box-shadow: 0 8px 32px rgba(99, 102, 241, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1) inset !important;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
      border: 1px solid rgba(255, 255, 255, 0.2) !important;
    }
    
    .crisp-client[data-crisp-loaded="true"] .crisp-1rjpbb7:hover,
    .crisp-client .cc-unoo:hover,
    div[class*="crisp-"][class*="unoo"]:hover {
      background: linear-gradient(135deg, rgba(79, 70, 229, 0.95), rgba(124, 58, 237, 0.95)) !important;
      box-shadow: 0 12px 48px rgba(99, 102, 241, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2) inset !important;
      transform: scale(1.05) translateY(-2px) !important;
    }
    
    /* ===== CHAT WINDOW CONTAINER ===== */
    /* Main chat window frame */
    .crisp-client .crisp-client-frame,
    .crisp-client .cc-kv6t,
    .crisp-client .crisp-1rf4xdh,
    div[class*="crisp-client-frame"] {
      background: rgba(15, 23, 42, 0.98) !important;
      backdrop-filter: blur(24px) saturate(180%) !important;
      -webkit-backdrop-filter: blur(24px) saturate(180%) !important;
      border: 1px solid rgba(99, 102, 241, 0.3) !important;
      border-radius: 20px !important;
      box-shadow: 
        0 25px 50px -12px rgba(0, 0, 0, 0.5),
        0 0 0 1px rgba(99, 102, 241, 0.2) inset !important;
      overflow: hidden !important;
    }
    
    /* ===== CHAT HEADER ===== */
    /* Header bar with gradient */
    .crisp-client .cc-1xry,
    .crisp-client .crisp-12w4w0o,
    .crisp-client [class*="crisp-"][class*="header"],
    div[class*="cc-1xry"] {
      background: linear-gradient(135deg, 
        rgba(99, 102, 241, 0.95) 0%, 
        rgba(139, 92, 246, 0.95) 50%,
        rgba(168, 85, 247, 0.95) 100%) !important;
      backdrop-filter: blur(12px) !important;
      -webkit-backdrop-filter: blur(12px) !important;
      border-bottom: 1px solid rgba(255, 255, 255, 0.15) !important;
      border-radius: 20px 20px 0 0 !important;
      padding: 16px !important;
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2) !important;
    }
    
    /* Header text - ensure visibility */
    .crisp-client .cc-1xry *,
    .crisp-client .crisp-12w4w0o * {
      color: white !important;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2) !important;
    }
    
    /* ===== MESSAGE BUBBLES ===== */
    /* Agent/incoming messages */
    .crisp-client .cc-1n5o,
    .crisp-client .crisp-1uw3v5k,
    .crisp-client [class*="message"][class*="visitor"],
    div[class*="cc-1n5o"] {
      background: rgba(99, 102, 241, 0.2) !important;
      backdrop-filter: blur(10px) !important;
      -webkit-backdrop-filter: blur(10px) !important;
      border: 1px solid rgba(99, 102, 241, 0.3) !important;
      border-radius: 16px 16px 16px 4px !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
      padding: 12px 16px !important;
      color: white !important;
    }
    
    /* User/outgoing messages */
    .crisp-client .cc-ge4t,
    .crisp-client .crisp-ftr8mu,
    .crisp-client [class*="message"][class*="operator"],
    div[class*="cc-ge4t"] {
      background: linear-gradient(135deg, 
        rgba(99, 102, 241, 0.85) 0%, 
        rgba(139, 92, 246, 0.85) 100%) !important;
      backdrop-filter: blur(10px) !important;
      -webkit-backdrop-filter: blur(10px) !important;
      border: 1px solid rgba(255, 255, 255, 0.2) !important;
      border-radius: 16px 16px 4px 16px !important;
      box-shadow: 0 4px 16px rgba(99, 102, 241, 0.4) !important;
      padding: 12px 16px !important;
      color: white !important;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
    }
    
    /* Message text color override */
    .crisp-client .cc-1n5o *,
    .crisp-client .cc-ge4t * {
      color: white !important;
    }
    
    /* ===== INPUT FIELD ===== */
    /* Composer/text input area */
    .crisp-client .cc-1v6c,
    .crisp-client .crisp-1o3lfyz,
    .crisp-client textarea[class*="crisp"],
    textarea[class*="cc-1v6c"] {
      background: rgba(30, 41, 59, 0.9) !important;
      backdrop-filter: blur(8px) !important;
      -webkit-backdrop-filter: blur(8px) !important;
      border: 1px solid rgba(99, 102, 241, 0.3) !important;
      border-radius: 14px !important;
      color: white !important;
      padding: 12px 16px !important;
      font-size: 14px !important;
    }
    
    .crisp-client .cc-1v6c:focus,
    .crisp-client .crisp-1o3lfyz:focus,
    textarea[class*="cc-1v6c"]:focus {
      border-color: rgba(99, 102, 241, 0.6) !important;
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15), 0 4px 12px rgba(99, 102, 241, 0.2) !important;
      outline: none !important;
    }
    
    .crisp-client .cc-1v6c::placeholder {
      color: rgba(255, 255, 255, 0.5) !important;
    }
    
    /* ===== SEND BUTTON ===== */
    /* Send message button */
    .crisp-client .cc-7hio,
    .crisp-client .crisp-1h2xhr5,
    .crisp-client button[class*="send"],
    button[class*="cc-7hio"] {
      background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%) !important;
      border: 1px solid rgba(255, 255, 255, 0.2) !important;
      border-radius: 10px !important;
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3) !important;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
      padding: 8px 16px !important;
    }
    
    .crisp-client .cc-7hio:hover,
    .crisp-client .crisp-1h2xhr5:hover,
    button[class*="cc-7hio"]:hover {
      background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%) !important;
      transform: scale(1.05) !important;
      box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4) !important;
    }
    
    /* ===== SCROLLBAR ===== */
    /* Custom scrollbar for messages */
    .crisp-client .cc-14ov::-webkit-scrollbar {
      width: 6px !important;
    }
    
    .crisp-client .cc-14ov::-webkit-scrollbar-track {
      background: rgba(30, 41, 59, 0.3) !important;
      border-radius: 3px !important;
    }
    
    .crisp-client .cc-14ov::-webkit-scrollbar-thumb {
      background: rgba(99, 102, 241, 0.5) !important;
      border-radius: 3px !important;
    }
    
    .crisp-client .cc-14ov::-webkit-scrollbar-thumb:hover {
      background: rgba(99, 102, 241, 0.7) !important;
    }
    
    /* ===== ANIMATIONS ===== */
    /* Smooth transitions for all interactive elements */
    .crisp-client * {
      transition: background 0.2s ease, 
                  border-color 0.2s ease, 
                  transform 0.2s ease,
                  box-shadow 0.2s ease !important;
    }
    
    /* ===== MOBILE OPTIMIZATIONS ===== */
    @media (max-width: 768px) {
      .crisp-client .crisp-client-frame,
      .crisp-client .cc-kv6t {
        border-radius: 16px !important;
        margin: 8px !important;
      }
      
      .crisp-client .cc-1xry,
      .crisp-client .crisp-12w4w0o {
        border-radius: 16px 16px 0 0 !important;
        padding: 12px !important;
      }
      
      /* Ensure chat takes full width on mobile */
      .crisp-client[data-crisp-loaded="true"] {
        max-width: 100vw !important;
      }
    }
    
    /* ===== DARK MODE ENHANCEMENTS ===== */
    /* Additional contrast for dark backgrounds */
    @media (prefers-color-scheme: dark) {
      .crisp-client .crisp-client-frame,
      .crisp-client .cc-kv6t {
        background: rgba(10, 15, 30, 0.98) !important;
        border-color: rgba(99, 102, 241, 0.4) !important;
      }
    }
  `;
  
  document.head.appendChild(style);
  console.log('[CrispChat] Custom CSS injected with enhanced specificity');
}

// Set up mutation observer to watch for Crisp DOM changes and re-apply styles
function setupCrispStyleObserver() {
  // Check if observer already exists
  if ((window as any)._crispStyleObserver) {
    console.log('[CrispChat] Style observer already running');
    return;
  }

  console.log('[CrispChat] Setting up DOM mutation observer for Crisp');

  const observer = new MutationObserver((mutations) => {
    // Check if Crisp elements were added
    const hasCrispChanges = mutations.some(mutation => 
      Array.from(mutation.addedNodes).some(node => {
        if (!(node instanceof Element)) return false;
        
        // Safely check className (could be string, SVGAnimatedString, or undefined)
        let classNameStr = '';
        try {
          if (typeof node.className === 'string') {
            classNameStr = node.className;
          } else if (node.className && typeof (node.className as any).baseVal === 'string') {
            classNameStr = (node.className as any).baseVal;
          }
        } catch (e) {
          // Ignore className errors
        }
        
        return (
          node.classList?.contains('crisp-client') ||
          classNameStr.includes('crisp') ||
          classNameStr.includes('cc-') ||
          node.id?.includes('crisp')
        );
      })
    );

    if (hasCrispChanges) {
      console.log('[CrispChat] Crisp DOM changed, re-applying styles');
      injectCustomCrispCSS();
    }
  });

  // Watch for changes to body
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  // Store observer reference
  (window as any)._crispStyleObserver = observer;
  
  console.log('[CrispChat] Style observer started');
}

export default CrispChat;
